import React, { useEffect } from "react";
import styled from "styled-components";

import images from "../../../resources";

const Page = styled.div`
  min-height: 100vh;
  overflow-x: hidden;
  background: var(--color-bg);
  color: var(--color-text);
`;

const Shell = styled.div`
  width: min(1160px, calc(100% - 48px));
  margin: 0 auto;

  @media (max-width: 640px) {
    width: min(100% - 32px, 1160px);
  }
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 10;
  border-bottom: 1px solid var(--color-border);
  background: rgba(7, 8, 8, 0.92);
  backdrop-filter: blur(18px);
`;

const HeaderInner = styled(Shell)`
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-5);
`;

const Brand = styled.a`
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
  color: var(--color-text) !important;
  font-family: var(--font-sans);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  letter-spacing: -0.02em;

  &:hover {
    text-decoration: none !important;
  }
`;

const BrandMark = styled.img`
  width: 38px;
  height: 38px;
  border-radius: var(--radius-md);
`;

const HeaderNav = styled.nav`
  display: flex;
  align-items: center;
  gap: var(--space-5);

  @media (max-width: 720px) {
    a:not(:last-child) {
      display: none;
    }
  }
`;

const NavLink = styled.a`
  color: var(--color-text-secondary) !important;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);

  &:hover {
    color: var(--color-text) !important;
    text-decoration: none !important;
  }
`;

const NavAction = styled(NavLink)`
  min-height: 40px;
  padding: 0 var(--space-4);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-inverse) !important;
  background: var(--color-accent);
  border: 1px solid var(--color-accent);
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-semibold);

  &:hover {
    color: var(--color-text-inverse) !important;
    background: var(--color-accent-hover);
    border-color: var(--color-accent-hover);
  }
`;

const Hero = styled.main`
  position: relative;
  border-bottom: 1px solid var(--color-border);

  &::before {
    content: "";
    position: absolute;
    width: 660px;
    height: 660px;
    left: 50%;
    top: -330px;
    border-radius: 50%;
    background: radial-gradient(circle, var(--color-accent-subtle), transparent 68%);
    transform: translateX(-50%);
    pointer-events: none;
  }
`;

const HeroGrid = styled(Shell)`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(360px, 0.95fr);
  align-items: center;
  gap: var(--space-16);
  min-height: 680px;
  padding-top: var(--space-20);
  padding-bottom: var(--space-20);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: var(--space-12);
    min-height: auto;
  }

  @media (max-width: 640px) {
    padding-top: var(--space-16);
    padding-bottom: var(--space-16);
  }
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: var(--space-6);
  color: var(--color-accent-text);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.12em;
  text-transform: uppercase;

  &::before {
    content: "";
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--color-accent);
    box-shadow: 0 0 0 5px var(--color-accent-subtle);
  }
`;

const HeroTitle = styled.h1`
  max-width: 760px;
  font-size: clamp(42px, 6.4vw, 76px) !important;
  line-height: 0.98 !important;
  letter-spacing: -0.045em !important;
`;

const Accent = styled.span`
  color: var(--color-accent-text);
`;

const HeroCopy = styled.p`
  max-width: 650px;
  margin-top: var(--space-6) !important;
  color: var(--color-text-secondary);
  font-size: clamp(17px, 2vw, 20px);
  line-height: var(--line-height-relaxed);
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
  margin-top: var(--space-8);
`;

const PrimaryLink = styled.a`
  min-height: 48px;
  padding: 0 var(--space-5);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-inverse) !important;
  background: var(--color-accent);
  border: 1px solid var(--color-accent);
  border-radius: var(--radius-md);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);

  &:hover {
    color: var(--color-text-inverse) !important;
    background: var(--color-accent-hover);
    border-color: var(--color-accent-hover);
    text-decoration: none !important;
  }
`;

const SecondaryLink = styled(PrimaryLink)`
  color: var(--color-text) !important;
  background: var(--color-surface);
  border-color: var(--color-border-strong);

  &:hover {
    color: var(--color-text) !important;
    background: var(--color-surface-subtle);
    border-color: var(--color-border-strong);
  }
`;

const ScopeNote = styled.p`
  margin-top: var(--space-5) !important;
  color: var(--color-text-tertiary);
  font-family: var(--font-mono);
  font-size: var(--font-size-xs);
`;

const ProductFrame = styled.div`
  position: relative;
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-xl);
  background: var(--color-surface-inset);
  box-shadow: var(--shadow-lg);
  padding: var(--space-3);

  &::after {
    content: "";
    position: absolute;
    inset: -1px;
    border-radius: inherit;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
    pointer-events: none;
  }
`;

const ProductWindow = styled.div`
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
`;

const WindowBar = styled.div`
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--space-4);
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
`;

const WindowDots = styled.div`
  display: flex;
  gap: 6px;

  span {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--color-border-strong);
  }
`;

const Preview = styled.div`
  padding: var(--space-5);
`;

const PreviewLabel = styled.div`
  color: var(--color-text-tertiary);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const PreviewTitle = styled.div`
  margin-top: var(--space-2);
  font-family: var(--font-display);
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-semibold);
`;

const Slides = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-2);
  margin-top: var(--space-5);
`;

const Slide = styled.div<{ $accent?: boolean }>`
  aspect-ratio: 4 / 5;
  display: flex;
  align-items: flex-end;
  padding: var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: ${({ $accent }) =>
    $accent
      ? "linear-gradient(145deg, var(--color-accent-subtle), var(--color-surface-subtle))"
      : "var(--color-surface-subtle)"};
  color: var(--color-text-secondary);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
`;

const PublishRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  margin-top: var(--space-5);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-border);
`;

const Connected = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);

  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--color-success);
  }
`;

const MockButton = styled.div`
  min-height: 38px;
  padding: 0 var(--space-4);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md);
  background: var(--color-accent);
  color: var(--color-text-inverse);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const Section = styled.section`
  padding: var(--space-24) 0;
  border-bottom: 1px solid var(--color-border);

  @media (max-width: 640px) {
    padding: var(--space-16) 0;
  }
`;

const SectionHead = styled.div`
  max-width: 720px;
`;

const SectionKicker = styled.div`
  margin-bottom: var(--space-4);
  color: var(--color-accent-text);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const SectionTitle = styled.h2`
  font-size: clamp(34px, 5vw, 52px) !important;
  letter-spacing: -0.035em !important;
`;

const SectionCopy = styled.p`
  margin-top: var(--space-5) !important;
  color: var(--color-text-secondary);
  font-size: var(--font-size-lg);
  line-height: var(--line-height-relaxed);
`;

const Steps = styled.ol`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-5);
  margin: var(--space-12) 0 0 !important;
  padding: 0;
  list-style: none;

  @media (max-width: 780px) {
    grid-template-columns: 1fr;
  }
`;

const Step = styled.li`
  min-height: 250px;
  display: flex;
  flex-direction: column;
  padding: var(--space-6);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
`;

const StepNumber = styled.div`
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-full);
  color: var(--color-accent-text);
  font-family: var(--font-mono);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const StepTitle = styled.h3`
  margin-top: auto !important;
  padding-top: var(--space-10);
  font-family: var(--font-sans) !important;
  font-size: var(--font-size-xl) !important;
`;

const StepCopy = styled.p`
  margin-top: var(--space-3) !important;
  color: var(--color-text-secondary);
  line-height: var(--line-height-relaxed);
`;

const SafetyGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
  gap: var(--space-16);
  align-items: start;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: var(--space-10);
  }
`;

const Principles = styled.div`
  display: grid;
  gap: var(--space-3);
`;

const Principle = styled.div`
  display: grid;
  grid-template-columns: 24px 1fr;
  gap: var(--space-4);
  padding: var(--space-5);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
`;

const Check = styled.div`
  width: 22px;
  height: 22px;
  display: grid;
  place-items: center;
  border-radius: var(--radius-full);
  background: var(--color-success-subtle);
  color: var(--color-success);
  font-size: 13px;
  font-weight: var(--font-weight-bold);
`;

const PrincipleTitle = styled.h3`
  font-family: var(--font-sans) !important;
  font-size: var(--font-size-md) !important;
`;

const PrincipleCopy = styled.p`
  margin-top: var(--space-2) !important;
  color: var(--color-text-secondary);
  line-height: var(--line-height-relaxed);
`;

const ReviewPanel = styled.div`
  padding: var(--space-8);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-xl);
  background:
    linear-gradient(145deg, var(--color-accent-subtle), transparent 56%),
    var(--color-surface);
`;

const ReviewLabel = styled.div`
  color: var(--color-accent-text);
  font-family: var(--font-mono);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const ReviewTitle = styled.h2`
  margin-top: var(--space-5) !important;
  font-size: clamp(28px, 4vw, 42px) !important;
`;

const ReviewCopy = styled.p`
  margin-top: var(--space-4) !important;
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
  line-height: var(--line-height-relaxed);
`;

const ReviewLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  margin-top: var(--space-6);
`;

const Footer = styled.footer`
  padding: var(--space-10) 0;
`;

const FooterInner = styled(Shell)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-5);
  color: var(--color-text-tertiary);
  font-size: var(--font-size-sm);

  @media (max-width: 720px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const FooterLinks = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-5);

  a {
    color: var(--color-text-secondary) !important;
  }
`;

const TikTokProductPage: React.FC = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "TikTok publishing with Tribed";

    const description = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]'
    );
    const previousDescription = description?.content;
    const meta = description || document.createElement("meta");
    if (!description) {
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content =
      "Tribed lets coaches connect their own TikTok account and send original photo carousels to TikTok as drafts for final review and posting in the TikTok app.";

    const existingCanonical = document.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]'
    );
    const previousCanonical = existingCanonical?.href;
    const canonical = existingCanonical || document.createElement("link");
    if (!existingCanonical) {
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://admin.tribed.io/tiktok";

    return () => {
      document.title = previousTitle;
      if (description && previousDescription !== undefined) {
        description.content = previousDescription;
      } else {
        meta.remove();
      }
      if (existingCanonical && previousCanonical) {
        existingCanonical.href = previousCanonical;
      } else {
        canonical.remove();
      }
    };
  }, []);

  return (
    <Page className="ds-root">
      <Header>
        <HeaderInner>
          <Brand href="https://tribed.io" aria-label="Tribed home">
            <BrandMark src={images.logo_letters} alt="" />
            Tribed
          </Brand>
          <HeaderNav aria-label="Public navigation">
            <NavLink href="#how-it-works">How it works</NavLink>
            <NavLink href="#safety">Safety</NavLink>
            <NavAction href="/">Open Tribed</NavAction>
          </HeaderNav>
        </HeaderInner>
      </Header>

      <Hero>
        <HeroGrid>
          <div>
            <Eyebrow>TikTok Content Posting integration</Eyebrow>
            <HeroTitle>
              Create in Tribed. <Accent>Finish in TikTok.</Accent>
            </HeroTitle>
            <HeroCopy>
              Coaches use Tribed to create original social content, connect their
              own TikTok account, and send photo carousels to their TikTok inbox
              as drafts. The coach makes the final review and publishes inside
              TikTok.
            </HeroCopy>
            <HeroActions>
              <PrimaryLink href="#how-it-works">See the complete flow</PrimaryLink>
              <SecondaryLink href="mailto:hello@tribed.io">
                Contact Tribed
              </SecondaryLink>
            </HeroActions>
            <ScopeNote>
              TikTok scopes used: user.info.basic · video.upload
            </ScopeNote>
          </div>

          <ProductFrame aria-label="Illustration of the Tribed Media Studio">
            <ProductWindow>
              <WindowBar>
                <span>Media Studio · Carousel</span>
                <WindowDots aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </WindowDots>
              </WindowBar>
              <Preview>
                <PreviewLabel>Ready to share</PreviewLabel>
                <PreviewTitle>Three lessons worth saving</PreviewTitle>
                <Slides aria-hidden="true">
                  <Slide $accent>01 · Hook</Slide>
                  <Slide>02 · Insight</Slide>
                  <Slide>03 · Action</Slide>
                </Slides>
                <PublishRow>
                  <Connected>
                    <span aria-hidden="true" /> TikTok connected
                  </Connected>
                  <MockButton>Send draft to TikTok</MockButton>
                </PublishRow>
              </Preview>
            </ProductWindow>
          </ProductFrame>
        </HeroGrid>
      </Hero>

      <Section id="how-it-works">
        <Shell>
          <SectionHead>
            <SectionKicker>How it works</SectionKicker>
            <SectionTitle>One explicit action at every step.</SectionTitle>
            <SectionCopy>
              Tribed supports a coach-owned workflow from connection through
              final publication. It does not silently publish content to a
              creator&apos;s profile.
            </SectionCopy>
          </SectionHead>
          <Steps>
            <Step>
              <StepNumber>01</StepNumber>
              <StepTitle>Connect an account</StepTitle>
              <StepCopy>
                The coach starts TikTok Login Kit from Media Studio, signs in to
                TikTok, and approves the requested access.
              </StepCopy>
            </Step>
            <Step>
              <StepNumber>02</StepNumber>
              <StepTitle>Choose original content</StepTitle>
              <StepCopy>
                The coach selects a photo carousel they created in Tribed and
                chooses “Send draft to TikTok.”
              </StepCopy>
            </Step>
            <Step>
              <StepNumber>03</StepNumber>
              <StepTitle>Review and post in TikTok</StepTitle>
              <StepCopy>
                TikTok receives the carousel as a draft. The coach opens TikTok,
                reviews the content, and decides whether to publish it.
              </StepCopy>
            </Step>
          </Steps>
        </Shell>
      </Section>

      <Section id="safety">
        <Shell>
          <SafetyGrid>
            <SectionHead>
              <SectionKicker>Control and privacy</SectionKicker>
              <SectionTitle>The connected creator stays in control.</SectionTitle>
              <SectionCopy>
                Tribed requests only the TikTok capabilities needed to identify
                the connected account and upload a creator-selected draft.
              </SectionCopy>
            </SectionHead>
            <Principles>
              <Principle>
                <Check aria-hidden="true">✓</Check>
                <div>
                  <PrincipleTitle>No direct posting</PrincipleTitle>
                  <PrincipleCopy>
                    Tribed uses TikTok&apos;s upload-to-draft flow. Publication is
                    completed by the coach in the TikTok app.
                  </PrincipleCopy>
                </div>
              </Principle>
              <Principle>
                <Check aria-hidden="true">✓</Check>
                <div>
                  <PrincipleTitle>Server-side credential storage</PrincipleTitle>
                  <PrincipleCopy>
                    TikTok access credentials are held in server-only storage and
                    are not exposed to the browser or community members.
                  </PrincipleCopy>
                </div>
              </Principle>
              <Principle>
                <Check aria-hidden="true">✓</Check>
                <div>
                  <PrincipleTitle>Revocable connection</PrincipleTitle>
                  <PrincipleCopy>
                    A coach can disconnect TikTok from Tribed settings, revoking
                    the stored connection before reconnecting later.
                  </PrincipleCopy>
                </div>
              </Principle>
              <Principle>
                <Check aria-hidden="true">✓</Check>
                <div>
                  <PrincipleTitle>Creator-selected media</PrincipleTitle>
                  <PrincipleCopy>
                    Only the carousel the coach explicitly selects is submitted
                    to the TikTok Content Posting API.
                  </PrincipleCopy>
                </div>
              </Principle>
            </Principles>
          </SafetyGrid>
        </Shell>
      </Section>

      <Section id="support">
        <Shell>
          <ReviewPanel>
            <ReviewLabel>Public product information</ReviewLabel>
            <ReviewTitle>Tribed is a platform for independent coaches.</ReviewTitle>
            <ReviewCopy>
              Coaches use Tribed to build branded community apps, create learning
              content, manage members, and prepare social media assets. TikTok
              publishing is one optional Media Studio integration. Questions
              about the integration, privacy, or account access can be sent to
              hello@tribed.io.
            </ReviewCopy>
            <ReviewLinks>
              <PrimaryLink href="https://tribed.io">About Tribed</PrimaryLink>
              <SecondaryLink href="https://tribed.io/showcase">
                View public apps
              </SecondaryLink>
            </ReviewLinks>
          </ReviewPanel>
        </Shell>
      </Section>

      <Footer>
        <FooterInner>
          <span>© 2026 Tribed. All rights reserved.</span>
          <FooterLinks aria-label="Legal and support">
            <a href="https://tribed.io/privacy">Privacy Policy</a>
            <a href="https://tribed.io/terms">Terms of Service</a>
            <a href="mailto:hello@tribed.io">Contact</a>
            <a href="https://tribed.io">Official website</a>
          </FooterLinks>
        </FooterInner>
      </Footer>
    </Page>
  );
};

export default TikTokProductPage;
